import { useState } from 'react'
import { AlertTriangle, CloudOff, RefreshCw, Trash2, WifiOff } from 'lucide-react'
import { useOfflineQueue } from '@/hooks/useOfflineQueue'
import { useI18n } from '@/i18n/I18nProvider'
import { useConfirm } from '@/components/ConfirmDialog'
import { Badge, Button, Modal } from './ui'
import { formatRelative } from '@/lib/utils'

/**
 * Connection and queue status.
 *
 * Mounted once in AppShell. It stays out of the way when there is nothing to
 * say — no banner when online with an empty queue — and becomes prominent
 * exactly when a worker needs to know their report has not left the device yet.
 */
export function OfflineQueueBar() {
  const { pending, failed, isOnline, isSyncing, sync, discard, retry } = useOfflineQueue()
  const confirm = useConfirm()
  const { t, tp } = useI18n()
  const [open, setOpen] = useState(false)

  const hasQueue = pending.length + failed.length > 0

  if (isOnline && !hasQueue) return null

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={
          failed.length > 0
            ? 'flex w-full items-center gap-2 bg-rose-600 px-4 py-2 text-sm font-medium text-white'
            : isOnline
              ? 'flex w-full items-center gap-2 bg-amber-500 px-4 py-2 text-sm font-medium text-white'
              : 'flex w-full items-center gap-2 bg-slate-700 px-4 py-2 text-sm font-medium text-white'
        }
      >
        {failed.length > 0 ? (
          <>
            <AlertTriangle className="size-4 shrink-0" aria-hidden />
            <span className="flex-1 text-left">{tp('queue.rejected', failed.length)}</span>
          </>
        ) : !isOnline ? (
          <>
            <WifiOff className="size-4 shrink-0" aria-hidden />
            {/*
              Two whole sentences joined by a space, which is safe in a way
              gluing a phrase together is not — neither half depends on the
              other's grammar, so a translator can rewrite either freely.
            */}
            <span className="flex-1 text-left">
              {t('queue.noConnection')}{' '}
              {pending.length > 0
                ? tp('queue.pending', pending.length)
                : t('queue.savedOnDevice')}
            </span>
          </>
        ) : (
          <>
            <CloudOff className="size-4 shrink-0" aria-hidden />
            <span className="flex-1 text-left">{tp('queue.pending', pending.length)}</span>
          </>
        )}

        <span className="shrink-0 underline">{t('queue.details')}</span>
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title={t('queue.title')}>
        <p className="muted mb-4 text-sm">
          {isOnline ? t('queue.hint') : t('queue.offlineNotice')}
        </p>

        {!hasQueue && <p className="muted text-sm">{t('queue.empty')}</p>}

        <ul className="space-y-2">
          {[...failed, ...pending].map((item) => (
            <li key={item.id} className="rounded-xl bg-[var(--surface-sunken)] p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{item.typeLabel}</p>
                  <p className="muted truncate text-xs">
                    {item.workCenterName} · {formatRelative(new Date(item.createdAt).toISOString())}
                    {item.files.length > 0 &&
                      ` · ${t('queue.attachments', { count: item.files.length })}`}
                  </p>
                </div>

                <Badge color={item.status === 'failed' ? 'rose' : 'amber'}>
                  {item.status === 'failed' ? t('queue.rejectedBadge') : t('queue.waiting')}
                </Badge>
              </div>

              {item.payload.description && (
                <p className="muted mt-1.5 line-clamp-2 text-xs">{item.payload.description}</p>
              )}

              {item.lastError && (
                <p className="mt-1.5 text-xs text-rose-600 dark:text-rose-400">{item.lastError}</p>
              )}

              <div className="mt-2 flex gap-1.5">
                {item.status === 'failed' && (
                  <Button variant="secondary" onClick={() => retry(item.id)}>
                    <RefreshCw className="size-3.5" aria-hidden />
                    {t('action.retry')}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  onClick={async () => {
                    if (await confirm({ title: t('queue.discardConfirm'), tone: 'danger' })) {
                      void discard(item.id)
                    }
                  }}
                >
                  <Trash2 className="size-3.5" aria-hidden />
                  {t('queue.remove')}
                </Button>
              </div>
            </li>
          ))}
        </ul>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setOpen(false)}>
            {t('action.close')}
          </Button>
          <Button onClick={() => sync(true)} loading={isSyncing} disabled={!isOnline}>
            {t('queue.sendNow')}
          </Button>
        </div>
      </Modal>
    </>
  )
}
